import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';
import { DomainSelector } from '../Common/DomainSelector';
import { UseCaseSelector } from './UseCaseSelector';
import { CadrageSelector } from './CadrageSelector';
import { Domain, UseCaseSummary, CadrageReadWithTeam } from '../../types';
import { readDocument } from '../../services/documentReaderService';
import notify from '../../lib/notify';

/** Supported output language codes for AI extraction (sent to GenieImporter). */
const OUTPUT_LANGUAGE_CODES = ['en', 'fr', 'nl', 'de', 'es', 'it'] as const;
type OutputLanguageCode = (typeof OUTPUT_LANGUAGE_CODES)[number];

function isOutputLanguageCode(lang: string): lang is OutputLanguageCode {
  return OUTPUT_LANGUAGE_CODES.includes(lang as OutputLanguageCode);
}

interface AIExtractionUploadProps {
  onUpload: (file: File | string, requestedAction: string, context?: Record<string, unknown>) => Promise<void>;
  isUploading?: boolean;
  /**
   * Optional initial text content (e.g. coming from a stored document in a Cadrage).
   * When provided, it will pre-fill the textarea.
   */
  initialText?: string;
}

const AIExtractionUpload: React.FC<AIExtractionUploadProps> = ({ onUpload, isUploading = false, initialText }) => {
  const { t, i18n } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [textContent, setTextContent] = useState<string>(initialText || '');
  const [requestedAction, setRequestedAction] = useState<string>('create_usecase');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [selectedUseCase, setSelectedUseCase] = useState<UseCaseSummary | null>(null);
  const [selectedCadrage, setSelectedCadrage] = useState<CadrageReadWithTeam | null>(null);
  const [isReadingFile, setIsReadingFile] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const defaultOutputLang: OutputLanguageCode = isOutputLanguageCode(i18n.language) ? i18n.language : 'en';
  const [outputLanguage, setOutputLanguage] = useState<OutputLanguageCode>(defaultOutputLang);

  // Sync initialText from navigation state (e.g. Cadrage -> AI Extraction)
  useEffect(() => {
    if (initialText && initialText.trim().length > 0) {
      setTextContent(initialText);
      setSelectedFile(null);
      setFileName('');
    }
  }, [initialText]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validExtensions = ['.pdf', '.docx', '.txt', '.md'];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validExtensions.includes(fileExt)) {
      notify.error(t('aiExtractionUpload.unsupportedFileType', { types: validExtensions.join(', ') }));
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      notify.error(t('aiExtractionUpload.fileTooLarge'));
      return;
    }

    // Send to backend to extract text
    setIsReadingFile(true);
    try {
      const response = await readDocument(file);
      
      if (response.success && response.content.trim()) {
        setTextContent(response.content);
        setSelectedFile(file);
        setFileName(response.filename || file.name);
        notify.success(response.word_count ? t('aiExtractionUpload.fileLoadedWords', { name: response.filename, count: response.word_count }) : t('aiExtractionUpload.fileLoaded', { name: response.filename }));
      } else {
        notify.warning(response.error || t('aiExtractionUpload.noTextExtracted'));
      }
    } catch (err) {
      console.error('Error reading file:', err);
      notify.error(t('aiExtractionUpload.fileReadError'));
    } finally {
      setIsReadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, []);

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextContent(event.target.value);
    // Only clear file if user is manually editing (not from file extraction)
    if (event.target.value !== textContent) {
      setSelectedFile(null);
      setFileName('');
    }
  }, [textContent]);

  const handleUpload = useCallback(async () => {
    if (!textContent.trim() || !selectedDomain) return;
    
    // Build context based on action
    const context: Record<string, string> = {
      domain_id: selectedDomain.id,
      output_language: outputLanguage,
    };

    if (requestedAction === 'create_usecase') {
      // No additional context needed
    } else if (requestedAction === 'update_usecase') {
      if (!selectedUseCase) return;
      context.usecase_id = selectedUseCase.id;
    } else if (requestedAction === 'create_cadrage') {
      if (!selectedUseCase) return;
      context.usecase_id = selectedUseCase.id;
    } else if (requestedAction === 'update_cadrage') {
      if (!selectedCadrage) return;
      context.cadrage_id = selectedCadrage.id;
      context.usecase_id = selectedCadrage.use_case_id;
    }
    
    try {
      // Always use text content (already extracted from file if uploaded)
      await onUpload(textContent, requestedAction, context);
      // Reset form
      setSelectedFile(null);
      setFileName('');
      setTextContent('');
      setSelectedDomain(null);
      setSelectedUseCase(null);
      setSelectedCadrage(null);
      setRequestedAction('create_usecase');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch {
      // Error handling is done in the parent component
    }
  }, [textContent, selectedDomain, selectedUseCase, selectedCadrage, requestedAction, onUpload]);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const canUpload = () => {
    if (!textContent.trim() || !selectedDomain) return false;
    
    if (requestedAction === 'create_usecase') return true;
    if (requestedAction === 'update_usecase') return !!selectedUseCase;
    if (requestedAction === 'create_cadrage') return !!selectedUseCase;
    if (requestedAction === 'update_cadrage') return !!selectedCadrage;
    
    return false;
  };

  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Left Column - Content Input (2/3 width) */}
      <div className="col-span-2 space-y-6">
        {/* Content Input Section */}
        <div data-tour="genie-content-input">
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('aiExtractionUpload.contentLabel')}
          </label>
          <div className="relative">
            <textarea
              value={textContent}
              onChange={handleTextChange}
              placeholder={t('aiExtractionUpload.contentPlaceholder')}
              className="w-full h-[500px] px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              disabled={isUploading || isReadingFile}
            />
            <div className="absolute top-4 right-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.md"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                type="button"
                onClick={triggerFileInput}
                disabled={isUploading || isReadingFile}
                className="bg-[#FF5733] hover:bg-[#E04826] text-white"
                data-tour="genie-import-button"
              >
                {isReadingFile ? t('aiExtractionUpload.reading') : t('aiExtractionUpload.importDocument')}
              </Button>
            </div>
          </div>
          {fileName && (
            <div className="mt-2 text-sm text-muted-foreground flex items-center justify-between">
              <span>
                📄 {fileName} {selectedFile && `(${(selectedFile.size / 1024).toFixed(2)} KB)`} - {t('aiExtractionUpload.extractedText')}
              </span>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setFileName('');
                  setTextContent('');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="text-destructive hover:text-destructive/80 text-xs"
              >
                {t('global.delete')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Selection Row (1/3 width) */}
      <div className="col-span-1 space-y-6">
        {/* Output language */}
        <div data-tour="genie-output-language">
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('aiExtraction.outputLanguage')}
          </label>
          <select
            value={outputLanguage}
            onChange={(e) => setOutputLanguage(e.target.value as OutputLanguageCode)}
            className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background truncate"
            disabled={isUploading}
          >
            <option value="en">{t('aiExtraction.outputLanguageEn')}</option>
            <option value="fr">{t('aiExtraction.outputLanguageFr')}</option>
            <option value="nl">{t('aiExtraction.outputLanguageNl')}</option>
            <option value="de">{t('aiExtraction.outputLanguageDe')}</option>
            <option value="es">{t('aiExtraction.outputLanguageEs')}</option>
            <option value="it">{t('aiExtraction.outputLanguageIt')}</option>
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('aiExtraction.outputLanguageHelp')}
          </p>
        </div>

        {/* Domain Selection */}
        <div data-tour="genie-domain-selector">
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('aiExtractionUpload.domain')}
          </label>
          <DomainSelector
            selectedDomainId={selectedDomain?.id || null}
            onDomainChange={setSelectedDomain}
            className="w-full"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('aiExtractionUpload.selectDomain')}
          </p>
        </div>

        {/* Action Selection - Only show when domain is selected */}
        {selectedDomain && (
          <div data-tour="genie-action-selector">
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('aiExtractionUpload.action')}
            </label>
            <select
              value={requestedAction}
              onChange={(e) => {
                setRequestedAction(e.target.value);
                setSelectedUseCase(null);
                setSelectedCadrage(null);
              }}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background truncate"
              disabled={isUploading}
            >
              <option value="create_usecase" className="truncate">{t('aiExtractionUpload.createUseCase')}</option>
              <option value="update_usecase" className="truncate">{t('aiExtractionUpload.updateUseCase')}</option>
              <option value="create_cadrage" className="truncate">{t('aiExtractionUpload.createCadrage')}</option>
              <option value="update_cadrage" className="truncate">{t('aiExtractionUpload.updateCadrage')}</option>
            </select>
          </div>
        )}

        {/* UseCase Selection (for update_usecase, create_cadrage, update_cadrage) */}
        {(requestedAction === 'update_usecase' || requestedAction === 'create_cadrage' || requestedAction === 'update_cadrage') && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('aiExtractionUpload.useCase')}
            </label>
            <UseCaseSelector
              domainId={selectedDomain?.id || null}
              selectedUseCaseId={selectedUseCase?.id || null}
              onUseCaseChange={setSelectedUseCase}
              disabled={isUploading}
            />
          </div>
        )}

        {/* Cadrage Selection (for update_cadrage only) */}
        {requestedAction === 'update_cadrage' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('aiExtractionUpload.cadrage')}
            </label>
            <CadrageSelector
              useCaseId={selectedUseCase?.id || null}
              selectedCadrageId={selectedCadrage?.id || null}
              onCadrageChange={setSelectedCadrage}
              disabled={isUploading}
            />
          </div>
        )}

        {/* Launch Button */}
        <div className="mt-6">
          <Button
            onClick={handleUpload}
            disabled={!canUpload() || isUploading}
            className="w-full bg-[#FF5733] hover:bg-[#E04826] text-white py-3 rounded-lg text-lg"
            data-tour="genie-launch-button"
          >
            {isUploading ? t('aiExtractionUpload.processing') : t('aiExtractionUpload.launch')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIExtractionUpload;

